import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";

import { useAuth } from "@/providers";
import {
  FormPageHeading,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import { type AgentRecord } from "@/shared/domain/agents";
import { agentQueryKey, useCreateAgent } from "@/shared/domain/agents/hooks";
import { Goto } from "@/shared/navigation";

import { AgentForm } from "../AgentForm/AgentForm";

import styles from "./CreateAgent.module.css";

const TITLE = "Create agent";

/** Write a new agent and land on the page of the one that was made. */
export function CreateAgent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;
  const { mutateAsync: createAgent } = useCreateAgent();

  return (
    <PageLayout
      title={`Roboto - ${TITLE}`}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Agents}>Agents</Link>
          <span>{TITLE}</span>
        </PageBreadcrumbs>
      }
    >
      <div className={styles.page} data-testid="create-agent-page">
        <FormPageHeading
          title={TITLE}
          subtitle="An agent works through the goals you give it and reports what it found."
        />
        <AgentForm
          mode="create"
          onSubmit={async (request) => {
            if (orgId === undefined) {
              // Every write is org-scoped, and the submit is never disabled
              // for anything, so the one case the page cannot serve is
              // reported on the form rather than hidden behind a dead button.
              throw new Error(
                "No organization is selected, so there is nowhere to create this agent.",
              );
            }
            const created = await createAgent({ orgId, request });
            // The response is the stored agent, so the page it lands on has
            // nothing left to fetch before its first paint.
            queryClient.setQueryData<AgentRecord>(
              agentQueryKey(created.agent_id),
              created,
            );
            void navigate(Goto.createAgentRoute(created.agent_id));
          }}
          onCancel={() => {
            void navigate(Goto.route.Agents);
          }}
        />
      </div>
    </PageLayout>
  );
}
